import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { CreditsInfo, HubPlan } from "data/models";
import {
  ReactQueryMutationError,
  updateOverageLimit,
} from "data/mutations-common";
import { fetchCredits } from "data/queries-common";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roundDownOverageBudget } from "utils/calculations";
import { formatPrice } from "utils/format";
import { resolveIdentityId } from "utils/identity";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DollarSign, Info, TriangleAlert } from "lucide-react";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  InputGroupAddon,
  InputGroupInput,
  InputGroup,
} from "@/components/ui/input-group";

interface OverageFieldProps {
  currentOverage: CreditsInfo;
  plan: HubPlan;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const OverageField = (props: OverageFieldProps) => {
  const [newOverageBudget, setNewOverageBudget] = useState(
    props.currentOverage.overageDollarsLimit,
  );

  const queryClient = useQueryClient();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const creditsQuery = useQuery({
    queryKey: [QueryKeys.overage, identityId],
    queryFn: () => fetchCredits(),
  });

  const overageMutation = useMutation({
    mutationFn: () => {
      if (newOverageBudget === undefined) {
        throw Error(
          "Unable to update overage, incomplete overage information.",
        );
      }

      return updateOverageLimit(newOverageBudget, {
        overageRate: props.plan.overage_cost_dollars!,
        planCredits: props.plan.credit_amount,
      });
    },
    onSuccess: (newOverage) => {
      queryClient.setQueryData([QueryKeys.credits, identityId], newOverage);
      setNewOverageBudget(newOverage.overageDollarsLimit);
      if (props.onSubmit) {
        props.onSubmit();
      }
    },
  });

  const roundedBudget = roundDownOverageBudget(newOverageBudget, {
    overageRate: props.plan.overage_cost_dollars!,
    planCredits: props.plan.credit_amount,
  });

  const validLimit =
    creditsQuery.isSuccess &&
    creditsQuery.data &&
    newOverageBudget >= creditsQuery.data?.overageDollarsSpent;

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <Field>
          <FieldLabel>Overage Limit</FieldLabel>
          <InputGroup>
            <InputGroupInput
              placeholder="0"
              id="overage-limit"
              type="number"
              min={0}
              max={10000}
              className="px-7"
              defaultValue={formatPrice(
                creditsQuery.data?.overageDollarsLimit ?? 0,
              )}
              value={newOverageBudget}
              onChange={(event) => {
                setNewOverageBudget(
                  Math.min(parseInt(event.target.value), 10000),
                );
              }}
            />
            <InputGroupAddon>
              <DollarSign />
            </InputGroupAddon>
          </InputGroup>
        </Field>
        <Button
          disabled={!validLimit || overageMutation.isPending}
          onClick={() => {
            overageMutation.mutate();
          }}
        >
          {overageMutation.isPending ? (
            <>
              <Spinner />
              Save
            </>
          ) : (
            "Save"
          )}
        </Button>
        {props.onCancel && (
          <Button
            variant="outline"
            disabled={overageMutation.isPending}
            onClick={props.onCancel}
          >
            Cancel
          </Button>
        )}
      </div>
      {!Number.isNaN(newOverageBudget) &&
        newOverageBudget !== roundedBudget &&
        newOverageBudget % props.plan.overage_cost_dollars! !== 0 && (
          <Alert variant="info">
            <Info className="size-4" />
            <AlertDescription>
              The overage limit will automatically be rounded down to the
              nearest ${props.plan.overage_cost_dollars! * 1000} increment the
              equivalent of 1000 credits ({formatPrice(roundedBudget)}) when
              saved.
            </AlertDescription>
          </Alert>
        )}
      {creditsQuery.isSuccess &&
        creditsQuery.data &&
        !Number.isNaN(newOverageBudget) &&
        !validLimit && (
          <Alert variant="warning">
            <TriangleAlert className="size-4" />
            <AlertDescription>
              Cannot lower overage limit ({formatPrice(roundedBudget)}) to less
              than the current overage spend (
              {formatPrice(creditsQuery.data.overageDollarsSpent)})
            </AlertDescription>
          </Alert>
        )}
      {overageMutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            (overageMutation.error as ReactQueryMutationError).message
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
